import axiosInstance from './axios';
import { Employee, EmployeeDetail, PaginatedResponse, EmployeeStatus } from '../../types';

export interface CreateEmployeeDto {
  employeeCode: string;
  fullName: string;
  email: string;
  departmentId: number;
  managerId?: number;
  joinDate: string;
  status?: EmployeeStatus;
}

export interface UpdateEmployeeDto {
  employeeCode?: string;
  fullName?: string;
  email?: string;
  departmentId?: number;
  managerId?: number | null;
  joinDate?: string;
  status?: EmployeeStatus;
}

export interface EmployeeFilters {
  page?: number;
  pageSize?: number;
  q?: string;
  departmentId?: number;
  status?: EmployeeStatus;
}

export interface ImportEmployeeResult {
  success: number;
  failed: number;
  errors: Array<{
    row: number;
    employeeCode?: string;
    error: string;
  }>;
}

class EmployeesService {
  private basePath = '/employees';

  /**
   * Get all employees with pagination and filters
   */
  async getAll(filters?: EmployeeFilters): Promise<PaginatedResponse<Employee>> {
    const response = await axiosInstance.get<PaginatedResponse<Employee>>(this.basePath, {
      params: filters,
    });
    return response.data;
  }

  /**
   * Get employee by ID
   */
  async getById(id: number): Promise<EmployeeDetail> {
    const response = await axiosInstance.get<EmployeeDetail>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new employee
   */
  async create(data: CreateEmployeeDto): Promise<Employee> {
    const response = await axiosInstance.post<Employee>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an employee
   */
  async update(id: number, data: UpdateEmployeeDto): Promise<Employee> {
    const response = await axiosInstance.patch<Employee>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete an employee (soft delete - sets status to INACTIVE)
   */
  async delete(id: number): Promise<void> {
    await axiosInstance.delete(`${this.basePath}/${id}`);
  }

  /**
   * Get employee's subordinates
   */
  async getSubordinates(id: number): Promise<Employee[]> {
    const response = await axiosInstance.get<Employee[]>(`${this.basePath}/${id}/subordinates`);
    return response.data;
  }

  /**
   * Import employees from CSV/Excel data
   */
  async import(employees: CreateEmployeeDto[]): Promise<ImportEmployeeResult> {
    const response = await axiosInstance.post<ImportEmployeeResult>(
      `${this.basePath}/import`,
      { employees }
    );
    return response.data;
  }

  /**
   * Download import template
   */
  getImportTemplateUrl(): string {
    return `${axiosInstance.defaults.baseURL}${this.basePath}/import/template`;
  }
}

export const employeesService = new EmployeesService();
